import type { DraftMessageId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { toast } from "@redotech/redo-web/alert";
import { useCallback, useState } from "react";
import { catchApiErrors } from "../db/error-handling";

interface UseImageUploadOptions {
  getDraftId: () => DraftMessageId | undefined;
  ensureDraft: () => Promise<DraftMessageId>;
}

interface UseImageUploadResult {
  uploadImage: (file: File) => Promise<string | null>;
  isUploading: boolean;
}

/**
 * Hook for uploading an inline image to S3 via the draft attachment endpoint.
 * Ensures a draft exists before uploading, then returns the S3 URL to embed in the editor.
 */
export function useImageUpload({
  getDraftId,
  ensureDraft,
}: UseImageUploadOptions): UseImageUploadResult {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const [isUploading, setIsUploading] = useState(false);

  const uploadImage = useCallback(
    async (file: File): Promise<string | null> => {
      setIsUploading(true);
      try {
        const draftId = getDraftId() ?? (await ensureDraft());

        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(",")[1];
            if (!base64) {
              reject(new Error("Failed to read file"));
              return;
            }
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const client = effectApi.getClient();
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.attachment.addDraftAttachment({
              payload: {
                draftMessageId: draftId,
                file: base64Data,
                filename: file.name,
                mimeType: file.type,
                size: file.size,
              },
            }),
          ),
        );

        return result.attachment.url;
      } catch {
        toast("Failed to upload image", { variant: "error" });
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [getDraftId, ensureDraft, effectApi],
  );

  return { uploadImage, isUploading };
}
